// Assign Practice: pick content → set the rules → preview what students will see →
// publish (or save a draft). The preview comes from the server and renders through the
// same AssignmentRulesSummary as the student's My Assignments.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { T } from '../gamification/shared';
import { createLogger } from '../../utils/logger';
import AssignmentRulesSummary from '../assignments/AssignmentRulesSummary';
import { tagSummary } from './contentTags';
import {
  AssignableBundle,
  AssignmentPreview,
  ClassAssignmentDraft,
  createClassAssignment,
  fetchAssignableContent,
  previewClassAssignment,
} from '../../services/classService';
import { PRACTICE_TYPES, PracticeType } from '../../services/assignmentService';
import {
  errorMessage,
  errorStyle,
  hintStyle,
  inputStyle,
  labelStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  sectionHeadingStyle,
} from './classesStyles';

const logger = createLogger('AssignPracticeWizard');

type Step = 'content' | 'rules' | 'preview';
const STEPS: Step[] = ['content', 'rules', 'preview'];

interface Props {
  classId: string;
  onDone: () => void;
  onClose: () => void;
}

interface RulesForm {
  title: string;
  description: string;
  dueDate: string; // datetime-local value
  attempts: string;
  unlimitedAttempts: boolean;
  minutes: string;
  hintsAllowed: boolean;
  objectives: string; // one per line
  rubric: string;
  rubricMaxScore: string;
  accommodations: string;
  practiceType: PracticeType | '';
}

const fieldStyle: React.CSSProperties = { marginBottom: '0.9rem' };

/** Scales a teacher can grade on: the AI suggests, and the teacher gives, a score from 0 to one of these. */
const DEFAULT_RUBRIC_MAX_SCORE = 4;
const RUBRIC_SCALES = [4, 5, 10, 100] as const;

const AssignPracticeWizard: React.FC<Props> = ({ classId, onDone, onClose }) => {
  const { t } = useTranslation();
  const tagLine = (bundle: AssignableBundle) =>
    tagSummary(bundle.tags, d => t(`studentAdmin.content.difficulties.${d}`));
  const [step, setStep] = useState<Step>('content');
  const [content, setContent] = useState<AssignableBundle[] | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [form, setForm] = useState<RulesForm>({
    title: '',
    description: '',
    dueDate: '',
    attempts: '',
    unlimitedAttempts: false,
    minutes: '',
    hintsAllowed: false,
    objectives: '',
    rubric: '',
    rubricMaxScore: String(DEFAULT_RUBRIC_MAX_SCORE),
    accommodations: '',
    practiceType: '',
  });
  const [preview, setPreview] = useState<AssignmentPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAssignableContent(classId)
      .then(loaded => {
        setContent(loaded.bundles);
        // Start from the school's defaults; the teacher can change both. With no school
        // session length the field stays blank: each activity runs for its own length.
        setForm(f => ({
          ...f,
          attempts: f.attempts || String(loaded.default_max_attempts),
          minutes: f.minutes || (loaded.default_session_minutes ? String(loaded.default_session_minutes) : ''),
        }));
      })
      .catch(err => {
        logger.error('Could not load assignable content', err);
        setError(t('classAssignments.couldNotLoadContent'));
      });
  }, [classId, t]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const set = <K extends keyof RulesForm>(key: K, value: RulesForm[K]) => setForm(f => ({ ...f, [key]: value }));

  const draft = (): ClassAssignmentDraft => ({
    title: form.title.trim(),
    description: form.description.trim() || null,
    bundle_ids: picked,
    due_date: form.dueDate ? new Date(form.dueDate).toISOString() : null,
    max_attempts: form.unlimitedAttempts ? null : Number(form.attempts) || null,
    unlimited_attempts: form.unlimitedAttempts,
    session_minutes: Number(form.minutes) || null,
    hints_allowed: form.hintsAllowed,
    objectives: form.objectives.split('\n').map(line => line.trim()).filter(Boolean),
    rubric: form.rubric.trim() || null,
    rubric_max_score: Number(form.rubricMaxScore) || DEFAULT_RUBRIC_MAX_SCORE,
    accommodations: form.accommodations.trim() || null,
    practice_type: form.practiceType || null,
  });

  const rulesProblem = (): string | null => {
    if (!form.title.trim()) return t('classAssignments.titleRequired');
    const attempts = Number(form.attempts);
    if (!form.unlimitedAttempts && (!Number.isInteger(attempts) || attempts < 1 || attempts > 50)) {
      return t('classAssignments.attemptsRange');
    }
    const minutes = Number(form.minutes);
    if (form.minutes.trim() && (!Number.isInteger(minutes) || minutes < 1 || minutes > 15)) {
      return t('classAssignments.minutesRange');
    }
    if (form.dueDate && new Date(form.dueDate).getTime() <= Date.now()) return t('classAssignments.dueInPast');
    return null;
  };

  const goToPreview = async () => {
    const problem = rulesProblem();
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setPreview(await previewClassAssignment(classId, draft()));
      setStep('preview');
    } catch (err) {
      logger.error('Preview failed', err);
      setError(errorMessage(err, t('classAssignments.couldNotPreview')));
    } finally {
      setBusy(false);
    }
  };

  const save = async (publish: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await createClassAssignment(classId, draft(), publish);
      onDone();
    } catch (err) {
      logger.error('Could not save the assignment', err);
      setError(errorMessage(err, t('classAssignments.couldNotSave')));
      setBusy(false);
    }
  };

  const togglePicked = (id: string) =>
    setPicked(current => (current.includes(id) ? current.filter(x => x !== id) : [...current, id]));

  const title = t('classAssignments.assignPractice');

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          background: T.card,
          borderRadius: T.radius,
          padding: '1.5rem',
          width: '100%',
          maxWidth: 640,
          maxHeight: '90vh',
          overflowY: 'auto',
          fontFamily: T.font,
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
        }}
      >
        <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.2rem', color: T.text }}>{title}</h2>
        <p style={{ ...hintStyle, marginTop: 0, marginBottom: '1rem' }}>
          {t('classAssignments.stepOf', { step: STEPS.indexOf(step) + 1, total: STEPS.length })} ·{' '}
          {t(`classAssignments.steps.${step}`)}
        </p>
        {error && <div style={errorStyle} role="alert">{error}</div>}

        {step === 'content' && (
          <>
            {content === null && !error && <p style={hintStyle}>{t('classes.loading')}</p>}
            {content && content.length === 0 && <p style={hintStyle}>{t('classAssignments.noContent')}</p>}
            {content?.map(bundle => (
              <label
                key={bundle.id}
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: '0.6rem 0.75rem',
                  border: `1px solid ${picked.includes(bundle.id) ? T.primaryLight : T.border}`,
                  borderRadius: 8,
                  marginBottom: 8,
                  cursor: 'pointer',
                }}
              >
                <input type="checkbox" checked={picked.includes(bundle.id)} onChange={() => togglePicked(bundle.id)} />
                <span>
                  <strong style={{ fontSize: '0.9rem' }}>{bundle.name}</strong>
                  {bundle.items.length > 0 && (
                    <span style={{ ...hintStyle, display: 'block', margin: 0 }}>
                      {bundle.items.map(item => item.name).filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {tagLine(bundle) && (
                    <span style={{ ...hintStyle, display: 'block', margin: 0, fontStyle: 'italic' }}>{tagLine(bundle)}</span>
                  )}
                </span>
              </label>
            ))}
          </>
        )}

        {step === 'rules' && (
          <>
            <div style={fieldStyle}>
              <label htmlFor="assign-title" style={labelStyle}>{t('classAssignments.fields.title')} *</label>
              <input id="assign-title" style={{ ...inputStyle, width: '100%' }} maxLength={255} value={form.title} onChange={e => set('title', e.target.value)} />
            </div>
            <div style={fieldStyle}>
              <label htmlFor="assign-description" style={labelStyle}>{t('classAssignments.fields.instructions')}</label>
              <textarea id="assign-description" rows={2} style={{ ...inputStyle, width: '100%' }} maxLength={4000} value={form.description} onChange={e => set('description', e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={fieldStyle}>
                <label htmlFor="assign-due" style={labelStyle}>{t('classAssignments.fields.dueDate')}</label>
                <input id="assign-due" type="datetime-local" style={inputStyle} value={form.dueDate} onChange={e => set('dueDate', e.target.value)} />
              </div>
              <div style={fieldStyle}>
                <label htmlFor="assign-attempts" style={labelStyle}>{t('classAssignments.fields.attempts')}</label>
                <input
                  id="assign-attempts"
                  type="number"
                  min={1}
                  max={50}
                  style={{ ...inputStyle, width: 90 }}
                  disabled={form.unlimitedAttempts}
                  value={form.attempts}
                  onChange={e => set('attempts', e.target.value)}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', marginTop: 4 }}>
                  <input type="checkbox" checked={form.unlimitedAttempts} onChange={e => set('unlimitedAttempts', e.target.checked)} />
                  {t('classAssignments.fields.unlimited')}
                </label>
              </div>
              <div style={fieldStyle}>
                <label htmlFor="assign-minutes" style={labelStyle}>{t('classAssignments.fields.minutes')}</label>
                <input id="assign-minutes" type="number" min={1} max={15} style={{ ...inputStyle, width: 90 }} value={form.minutes} placeholder={t('classAssignments.minutesAuto')} onChange={e => set('minutes', e.target.value)} />
                <span style={{ ...hintStyle, display: 'block', margin: '4px 0 0' }}>{t('classAssignments.minutesHint')}</span>
              </div>
            </div>
            <div style={fieldStyle}>
              <label htmlFor="assign-practice-type" style={labelStyle}>{t('classAssignments.fields.practiceType')}</label>
              <select
                id="assign-practice-type"
                style={inputStyle}
                value={form.practiceType}
                onChange={e => set('practiceType', e.target.value as PracticeType | '')}
              >
                <option value="">{t('practiceTypes.default')}</option>
                {PRACTICE_TYPES.map(type => (
                  <option key={type} value={type}>{t(`practiceTypes.${type}`)}</option>
                ))}
              </select>
              <span style={{ ...hintStyle, display: 'block', margin: '4px 0 0' }}>
                {t(`practiceTypes.roles.${form.practiceType || 'default'}`)}
              </span>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', marginBottom: '0.9rem' }}>
              <input type="checkbox" checked={form.hintsAllowed} onChange={e => set('hintsAllowed', e.target.checked)} />
              {t('classAssignments.fields.hints')}
            </label>
            <div style={fieldStyle}>
              <label htmlFor="assign-objectives" style={labelStyle}>{t('classAssignments.fields.objectives')}</label>
              <textarea id="assign-objectives" rows={3} style={{ ...inputStyle, width: '100%' }} value={form.objectives} onChange={e => set('objectives', e.target.value)} placeholder={t('classAssignments.objectivesPlaceholder')} />
            </div>
            <div style={fieldStyle}>
              <label htmlFor="assign-rubric" style={labelStyle}>{t('classAssignments.fields.rubric')}</label>
              <textarea id="assign-rubric" rows={3} style={{ ...inputStyle, width: '100%' }} maxLength={4000} value={form.rubric} onChange={e => set('rubric', e.target.value)} />
            </div>
            <div style={fieldStyle}>
              <label htmlFor="assign-rubric-scale" style={labelStyle}>{t('classAssignments.fields.rubricScale')}</label>
              <select id="assign-rubric-scale" style={inputStyle} value={form.rubricMaxScore} onChange={e => set('rubricMaxScore', e.target.value)}>
                {RUBRIC_SCALES.map(max => (
                  <option key={max} value={String(max)}>{t('classAssignments.rubricScaleOption', { max })}</option>
                ))}
              </select>
              <span style={{ ...hintStyle, display: 'block', margin: '4px 0 0' }}>{t('classAssignments.rubricScaleHint')}</span>
            </div>
            <div style={fieldStyle}>
              <label htmlFor="assign-accommodations" style={labelStyle}>{t('classAssignments.fields.accommodations')}</label>
              <textarea id="assign-accommodations" rows={2} style={{ ...inputStyle, width: '100%' }} maxLength={2000} value={form.accommodations} onChange={e => set('accommodations', e.target.value)} />
            </div>
            <p style={hintStyle}>{t('classAssignments.aiHint')}</p>
          </>
        )}

        {step === 'preview' && preview && (
          <>
            <p style={{ ...hintStyle, marginTop: 0 }}>
              {t('classAssignments.previewIntro', { count: preview.recipients, className: preview.class_name })}
            </p>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, padding: '1rem' }}>
              <h3 style={sectionHeadingStyle}>{preview.title}</h3>
              {preview.description && <p style={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap' }}>{preview.description}</p>}
              <AssignmentRulesSummary rules={preview.rules} dueDate={preview.due_date} className={preview.class_name} />
              <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', margin: '10px 0 4px' }}>
                {t('classAssignments.practice')}
              </p>
              <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.85rem' }}>
                {preview.bundles.flatMap(bundle =>
                  bundle.items.map((item, i) => <li key={`${bundle.id}-${i}`}>{item.name || bundle.name}</li>)
                )}
              </ul>
            </div>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: '1.25rem', flexWrap: 'wrap' }}>
          <button type="button" style={secondaryButtonStyle} onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            {step !== 'content' && (
              <button
                type="button"
                style={secondaryButtonStyle}
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setStep(step === 'preview' ? 'rules' : 'content');
                }}
              >
                {t('classAssignments.back')}
              </button>
            )}
            {step === 'content' && (
              <button
                type="button"
                style={primaryButtonStyle}
                disabled={picked.length === 0}
                onClick={() => {
                  setError(null);
                  setStep('rules');
                }}
              >
                {t('classAssignments.next')}
              </button>
            )}
            {step === 'rules' && (
              <button type="button" style={primaryButtonStyle} disabled={busy} onClick={goToPreview}>
                {t('classAssignments.preview')}
              </button>
            )}
            {step === 'preview' && (
              <>
                <button type="button" style={secondaryButtonStyle} disabled={busy} onClick={() => save(false)}>
                  {t('classAssignments.saveDraft')}
                </button>
                <button type="button" style={primaryButtonStyle} disabled={busy} onClick={() => save(true)}>
                  {t('classAssignments.publish')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AssignPracticeWizard;
